import { useMediaQuery, useTheme } from '@mui/material';
import React, { useEffect, type JSX } from 'react';
import { ListChildComponentProps, VariableSizeList } from 'react-window';
import { NativeScroll } from 'components/native-scroll';

const LISTBOX_PADDING = 8; // px

type Option = { id: string };

type RenderRowProps = ListChildComponentProps;

const renderRow = (props: RenderRowProps) => {
  const { data, index, style } = props;
  return React.cloneElement(data[index], {
    style: {
      ...style,
      top: Number(style?.top || 0) + LISTBOX_PADDING,
    },
  });
};

type OuterElementProps = React.HTMLAttributes<HTMLElement> & { renderOption?: unknown };

const OuterElementContext = React.createContext<OuterElementProps>({});

// MUI 9 puts its own onScroll and style in the listbox props (MUI 4 did not). Spread over
// react-window's, they replaced its onScroll, so the list never re-rendered on scroll and went
// blank (BF-07): compose both handlers, keep react-window's style, keep renderOption off the DOM.
const OuterElementType = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>(
  ({ onScroll, style, ...props }, ref) => {
    const {
      onScroll: outerOnScroll,
      style: outerStyle,
      renderOption: _renderOption,
      ...outerProps
    } = React.useContext(OuterElementContext);
    const handleScroll = (event: React.UIEvent<HTMLDivElement>) => {
      onScroll?.(event);
      outerOnScroll?.(event);
    };
    return (
      <NativeScroll
        ref={ref}
        {...props}
        {...outerProps}
        style={{ ...outerStyle, ...style }}
        onScroll={handleScroll}
      />
    );
  },
);

const useResetCache = (data: any) => {
  const ref = React.useRef<VariableSizeList>(null);

  useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
};

type ListboxComponentProps = React.HTMLAttributes<HTMLElement> & {
  renderOption: <T extends Option>(option: T) => JSX.Element;
};

// Adapter for react-window
export const ListboxComponent = React.forwardRef<HTMLDivElement, ListboxComponentProps>(
  (props, ref) => {
    const { children, ...other } = props;
    const itemData: (React.ReactElement<any> | number | string)[] = [];
    (children as (React.ReactElement<any> | number | string)[]).forEach(
      (
        item: (React.ReactElement<any> | number | string) & {
          children?: (React.ReactElement<any> | number | string)[];
        },
      ) => {
        itemData.push(item);
        itemData.push(...(item.children || []));
      },
    );

    // const { isLtr } = useDetectLanguage();

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
      noSsr: true,
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child: React.ReactElement<any> | number | string) => {
      if (child.hasOwnProperty('group')) {
        return 48;
      }

      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            itemData={itemData}
            height={getHeight() + 2 * LISTBOX_PADDING}
            width="100%"
            ref={gridRef}
            outerElementType={OuterElementType}
            innerElementType="ul"
            itemSize={(index) => getChildSize(itemData[index])}
            overscanCount={5}
            itemCount={itemCount}
            // direction={isLtr ? 'ltr' : 'rtl'}
          >
            {renderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  },
);
